/**
 * Hit-stop and bait helpers shared by the controller, ball and contact modules (owner: contact team).
 *
 * HIT_STOP folding: one HIT_STOP event per (tick, actor) — several freezes requested for the same actor on the
 * same tick keep the longest (`frames`, `cause`, position of the longest). The fold remembers the event objects it
 * pushed THIS step only (`beginFxTick` runs at the top of `updateControllers`, before any freeze can be requested
 * in a step), so rollback resimulation of the same tick number never folds into a stale object.
 * Rules may call `addHitStop` too (e.g. the touchdown freeze in rulesPost) to share the fold.
 */
import { BAIT_WINDOW_FRAMES, HITSTOP } from '@hf/shared';
import { CommitKind, FEINT_BAIT_KEYS, FeintKind, type BaitKey } from '../enums';
import type { HitStopEvent } from '../events';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';

type HitStopCause = HitStopEvent['cause'];

/** Pushed HIT_STOP events of the current step: [player 0, player 1, game-level]. */
const hsEvents: (HitStopEvent | null)[] = [null, null, null];
let hsSink: SimEventSink = null;
let hsTick = -1;
/** Per-step "already emitted" bits, [player 0, player 1, game-level] (see `firstThisStep`). */
const onceBits = [0, 0, 0];

/** Event kinds that must be emitted at most once per (step, actor) by this team's modules. */
export const Once = {
  STAMINA_DENIED: 1 << 0,
  POST_HIT: 1 << 1,
  BALL_BOUNCE: 1 << 2,
  SPIN_DODGE: 1 << 3,
  WHIFF: 1 << 4,
  STUN: 1 << 5,
} as const;

/** Reset the per-step fold (called once per unfrozen step, first thing in updateControllers). */
export const beginFxTick = (): void => {
  hsEvents[0] = null;
  hsEvents[1] = null;
  hsEvents[2] = null;
  hsSink = null;
  hsTick = -1;
  onceBits[0] = 0;
  onceBits[1] = 0;
  onceBits[2] = 0;
};

/**
 * True the first time `kind` is requested for `actor` (-1 = game) in the current step, false afterwards. Keeps
 * (tick, type, actor) unique for events several code paths may raise on one tick.
 */
export const firstThisStep = (kind: number, actor: number): boolean => {
  const slot = actor === 0 ? 0 : actor === 1 ? 1 : 2;
  const bits = onceBits[slot]!;
  if ((bits & kind) !== 0) return false;
  onceBits[slot] = bits | kind;
  return true;
};

/**
 * Freeze the whole sim for `frames` ticks (the longest request wins) and emit/fold HIT_STOP.
 * `actor` = the player who caused it (tackler, catcher, faker…) or -1 (goalpost).
 */
export const addHitStop = (
  s: SimState,
  ev: SimEventSink,
  actor: number,
  frames: number,
  cause: HitStopCause,
  x: number,
  y: number,
): void => {
  if (frames > s.hitStop) s.hitStop = frames;
  if (!ev) return;
  const slot = actor === 0 ? 0 : actor === 1 ? 1 : 2;
  const prev = hsEvents[slot];
  if (prev && hsSink === ev && hsTick === s.tick) {
    if (frames > prev.frames) {
      prev.frames = frames;
      prev.cause = cause;
      prev.x = x;
      prev.y = y;
    }
    return;
  }
  if (hsTick !== s.tick || hsSink !== ev) {
    hsEvents[0] = null;
    hsEvents[1] = null;
    hsEvents[2] = null;
    hsSink = ev;
    hsTick = s.tick;
  }
  const e: HitStopEvent = { type: 'HIT_STOP', tick: s.tick, actor, frames, cause, x, y };
  hsEvents[slot] = e;
  ev.push(e);
};

// ── Feints, commitments and baits (ARCHITECTURE §5 "Bait rules") ───────────────────────────────────────

/** Record a feint (fake jump, stutter, hesitation, spin, pump fake, fake dive, fake rush, fast-fall). */
export const recordFeint = (s: SimState, p: PlayerSim, kind: FeintKind): void => {
  p.feintKind = kind;
  p.feintTick = s.tick;
};

/** Did `victim`'s current commitment start within the bait window after `faker`'s feint? */
const committedAfterFeint = (faker: PlayerSim, victim: PlayerSim): boolean => {
  if (faker.feintTick < 0 || victim.commitTick < 0) return false;
  const d = victim.commitTick - faker.feintTick;
  return d >= 0 && d <= BAIT_WINDOW_FRAMES;
};

/**
 * "WHAT A FAKE!": FAKE_SUCCESS (+ statBaits, which the rules turn into POWER.bait) and a short freeze. A feint
 * baits at most once (its kind is cleared), and the victim's commitment is consumed.
 */
export const fakeSuccess = (
  s: SimState,
  ev: SimEventSink,
  faker: PlayerSim,
  victim: PlayerSim,
  bait: BaitKey,
): void => {
  faker.statBaits++;
  faker.feintKind = FeintKind.NONE;
  victim.commitKind = CommitKind.NONE;
  if (ev)
    ev.push({
      type: 'FAKE_SUCCESS',
      tick: s.tick,
      actor: faker.index,
      bait,
      victim: victim.index,
      x: faker.x,
      y: faker.y,
    });
  addHitStop(s, ev, faker.index, HITSTOP.fakeSuccess, 'fake', faker.x, faker.y);
};

const baitKey = (k: FeintKind): BaitKey => FEINT_BAIT_KEYS[k] as BaitKey;

/**
 * Record a commitment (dive, leap, jump, hurdle, truck, spin). Baits that pay off at commit time are resolved by
 * `resolveCommitBaits` once both controllers ran, so the result never depends on the player order.
 */
export const recordCommit = (s: SimState, _ev: SimEventSink, p: PlayerSim, kind: CommitKind): void => {
  p.commitKind = kind;
  p.commitTick = s.tick;
};

/**
 * Commit-time baits, after BOTH controllers ran this tick (symmetric in the player index): a PUMP FAKE succeeds
 * as soon as the opponent leaves the ground (JUMP or LEAP) within the window after it — including a jump on the
 * same tick as the pump fake, whichever player's controller ran first.
 */
export const resolveCommitBaits = (s: SimState, ev: SimEventSink): void => {
  for (let i = 0; i < 2; i++) {
    const victim = s.players[i as 0 | 1];
    if (victim.commitTick !== s.tick) continue;
    const k = victim.commitKind;
    if (k !== CommitKind.JUMP && k !== CommitKind.LEAP) continue;
    const faker = s.players[i === 0 ? 1 : 0];
    if (faker.feintKind === FeintKind.PUMP_FAKE && committedAfterFeint(faker, victim))
      fakeSuccess(s, ev, faker, victim, 'pump_fake');
  }
};

/**
 * `victim`'s DIVE / LEAP / JUMP ended without tackling the faker (lunge whiffed, hurdle cleared, jump landed):
 * a carrier's fake jump, stutter or hesitation inside the window baited it.
 */
export const commitFailed = (s: SimState, ev: SimEventSink, victim: PlayerSim): void => {
  const k = victim.commitKind;
  if (k !== CommitKind.DIVE && k !== CommitKind.LEAP && k !== CommitKind.JUMP) return;
  const faker = s.players[victim.index === 0 ? 1 : 0];
  const f = faker.feintKind;
  if (
    (f === FeintKind.FAKE_JUMP || f === FeintKind.STUTTER || f === FeintKind.HESITATION) &&
    committedAfterFeint(faker, victim)
  ) {
    fakeSuccess(s, ev, faker, victim, baitKey(f));
    return;
  }
  victim.commitKind = CommitKind.NONE;
};

/** A lunge hit a spinning carrier's i-frames: the spin baited it if the lunge started inside the window. */
export const spinDodgeBait = (s: SimState, ev: SimEventSink, carrier: PlayerSim, lunger: PlayerSim): void => {
  const k = lunger.commitKind;
  if (
    carrier.feintKind === FeintKind.SPIN &&
    (k === CommitKind.DIVE || k === CommitKind.LEAP) &&
    committedAfterFeint(carrier, lunger)
  )
    fakeSuccess(s, ev, carrier, lunger, 'spin');
};

/**
 * The defender just tackled `carrier`: a fake dive / fake rush baited the carrier if he committed HURDLE / TRUCK /
 * SPIN inside the window after it and is tackled inside the window after that commitment. A fast-fall baited an
 * offense player who JUMPed (or hurdled) inside the window and is beaten while that jump is still live.
 */
export const defenderPayoffBait = (
  s: SimState,
  ev: SimEventSink,
  defender: PlayerSim,
  victim: PlayerSim,
  tackled: boolean,
): void => {
  const f = defender.feintKind;
  const k = victim.commitKind;
  if (f === FeintKind.NONE || k === CommitKind.NONE || !committedAfterFeint(defender, victim)) return;
  const since = s.tick - victim.commitTick;
  if (
    tackled &&
    (f === FeintKind.FAKE_DIVE || f === FeintKind.FAKE_RUSH) &&
    (k === CommitKind.HURDLE || k === CommitKind.TRUCK || k === CommitKind.SPIN) &&
    since <= BAIT_WINDOW_FRAMES
  ) {
    fakeSuccess(s, ev, defender, victim, baitKey(f));
    return;
  }
  if (
    f === FeintKind.FAST_FALL &&
    (k === CommitKind.JUMP || k === CommitKind.HURDLE) &&
    since <= 2 * BAIT_WINDOW_FRAMES
  )
    fakeSuccess(s, ev, defender, victim, 'fast_fall');
};
